<script lang="ts" setup>
import type { ComponentBlockTextFragment } from "~~/types/graphql";

import {
  Enum_Componentblocktext_Layout,
  Enum_Componentblocktext_Variation,
  Enum_Componentblocktext_Flipped,
} from "~~/types/graphql";

import Arrow from "@/assets/icons/arrow.svg?component";

const props = defineProps<{
  TextText: ComponentBlockTextFragment["TextText"];
  TextHeadline: ComponentBlockTextFragment["TextHeadline"];
  TextVariation: Enum_Componentblocktext_Variation;
  TextLayout: Enum_Componentblocktext_Layout;
  TextFlipped: ComponentBlockTextFragment["TextFlipped"];
  TextAnchor?: ComponentBlockTextFragment["TextAnchor"];
  TextLink?: ComponentBlockTextFragment["TextLink"];
  TextLinkExternal?: ComponentBlockTextFragment["TextLinkExternal"];
  TextLinkTarget?: ComponentBlockTextFragment["TextLinkTarget"];
  TextButtonText?: ComponentBlockTextFragment["TextButtonText"];
}>();

function link() {
  let linkUrl = false;

  if (props.TextLinkTarget === "external") {
    linkUrl = props.TextLinkExternal || false;
  }
  // Default option 'page'
  else {
    linkUrl = props.TextLink.data?.attributes?.Slug || false;

    if (linkUrl && props.TextAnchor) {
      linkUrl = linkUrl + "#" + props.TextAnchor;
    }
  }

  return linkUrl;
}
</script>
<template>
  <layout-block
    :class="{
      'bg-integer-black text-integer-white':
        props.TextVariation === Enum_Componentblocktext_Variation.Dark,
      'bg-integer-white text-integer-black':
        props.TextVariation === Enum_Componentblocktext_Variation.Light,
    }"
    class="text"
  >
    <article
      class="grid grid-cols-12 gap-x-0 gap-y-10 md:gap-x-15 md:gap-y-15"
      :class="{
        'xl:container':
          props.TextLayout === Enum_Componentblocktext_Layout.TextTwoColumn,
      }"
    >
      <h2
        v-if="props.TextHeadline"
        class="hyphens-auto"
        :class="{
          'col-start-1 col-end-13 text-h2-s md:text-h2 lg:col-end-13 xl:col-end-10':
            props.TextLayout === Enum_Componentblocktext_Layout.TextShifted,
          'col-start-1 col-end-13 text-h2-s md:text-h2 lg:col-end-8':
            props.TextLayout === Enum_Componentblocktext_Layout.TextTwoColumn,
          'col-span-full text-xs lg:col-span-4 xl:col-span-6':
            props.TextLayout === Enum_Componentblocktext_Layout.TextOneColumn,
          'lg:order-1 lg:text-right':
            props.TextFlipped === Enum_Componentblocktext_Flipped.Links &&
            props.TextLayout === Enum_Componentblocktext_Layout.TextOneColumn,
        }"
      >
        {{ props.TextHeadline }}
      </h2>
      <base-text
        :Text="props.TextText"
        :class="{
          'col-start-1 col-end-13 row-start-2 lg:col-start-6 xl:col-start-8':
            props.TextLayout === Enum_Componentblocktext_Layout.TextShifted,
          'col-span-full md:columns-2':
            props.TextLayout === Enum_Componentblocktext_Layout.TextTwoColumn,
          'col-span-full lg:col-span-8 xl:col-span-6':
            props.TextLayout === Enum_Componentblocktext_Layout.TextOneColumn,
          'lg:-order-1':
            props.TextFlipped === Enum_Componentblocktext_Flipped.Links &&
            props.TextLayout === Enum_Componentblocktext_Layout.TextOneColumn,
        }"
      />
    </article>
    <div
      v-if="TextButtonText && link()"
      class="mt-12 xs:flex xs:justify-center"
      :class="{
        'md:grid md:grid-cols-12 md:gap-x-15':
          props.TextFlipped === Enum_Componentblocktext_Flipped.Rechts &&
          props.TextLayout === Enum_Componentblocktext_Layout.TextOneColumn,
        'lg:grid lg:grid-cols-12 lg:gap-x-15':
          props.TextLayout === Enum_Componentblocktext_Layout.TextShifted,
        'lg:justify-start':
          props.TextFlipped === Enum_Componentblocktext_Flipped.Links &&
          props.TextLayout === Enum_Componentblocktext_Layout.TextOneColumn,
      }"
    >
      <div
        :class="{
          'md:col-start-5 md:col-end-13 lg:col-start-5 xl:col-start-7':
            props.TextFlipped === Enum_Componentblocktext_Flipped.Rechts &&
            props.TextLayout === Enum_Componentblocktext_Layout.TextOneColumn,
          'lg:col-start-6 lg:col-end-13 xl:col-start-8':
            props.TextLayout === Enum_Componentblocktext_Layout.TextShifted,
        }"
      >
        <base-button
          :to="link()"
          :target="props.TextLinkTarget === 'external' ? '_blank' : ''"
          transparent
          type="secondary"
          class="w-full items-center justify-center xs:w-auto"
          :class="{
            'border-integer-white text-integer-white hover:bg-integer-white hover:text-integer-black':
              props.TextVariation === 'Dark' || props.TextVariation === 'dark',
            'hover:border-integer-black':
              props.TextVariation === 'Light' ||
              props.TextVariation === 'light',
          }"
          >{{ TextButtonText }}<template #icon-right><Arrow /></template
        ></base-button>
      </div>
    </div>
  </layout-block>
</template>
